import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Check } from 'lucide-react';
type Product = {
  title: string;
  editionLabel?: string;
  priceNumber?: number;
  priceText?: string;
  thumbnailUrl?: string;
};
const FIRST_NAMES = ['Emma', 'Olivia', 'Sophia', 'Ava', 'Isabella', 'Mia', 'Charlotte', 'Amelia', 'Harper', 'Evelyn', 'Abigail', 'Ella', 'Scarlett', 'Grace', 'Chloe', 'Lily', 'Hannah', 'Layla', 'Nora', 'Zoe', 'Hazel', 'Riley', 'Aria', 'Stella', 'James', 'William', 'Benjamin', 'Lucas', 'Henry', 'Alexander', 'Michael', 'Daniel', 'Matthew', 'Jackson', 'David', 'Joseph', 'Samuel', 'Andrew', 'Logan', 'John', 'Owen', 'Wyatt', 'Dylan', 'Luke', 'Gabriel', 'Anthony', 'Patricia', 'Linda', 'Barbara', 'Susan', 'Jessica', 'Karen', 'Nancy', 'Margaret', 'Sandra', 'Dorothy', 'Carol', 'Ruth', 'Sharon', 'Michelle', 'Robert', 'Richard', 'Thomas', 'Charles', 'Christopher', 'Mark', 'Steven', 'Paul', 'Kevin', 'Brian', 'George', 'Edward', 'Ronald', 'Timothy', 'Jason'];
const LAST_INITIALS = ['A.', 'B.', 'C.', 'D.', 'F.', 'G.', 'H.', 'J.', 'K.', 'L.', 'M.', 'N.', 'P.', 'R.', 'S.', 'T.', 'W.'];
const US_CITIES = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'Indianapolis, IN', 'San Francisco, CA', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'Nashville, TN', 'Portland, OR', 'Las Vegas, NV', 'Detroit, MI', 'Memphis, TN', 'Louisville, KY', 'Baltimore, MD', 'Milwaukee, WI', 'Albuquerque, NM', 'Tucson, AZ', 'Sacramento, CA', 'Kansas City, MO', 'Atlanta, GA', 'Miami, FL', 'Raleigh, NC', 'Omaha, NE', 'Minneapolis, MN', 'Tulsa, OK', 'Cleveland, OH', 'Wichita, KS', 'Arlington, TX', 'New Orleans, LA', 'Bakersfield, CA', 'Tampa, FL', 'Honolulu, HI', 'Anaheim, CA', 'Santa Ana, CA', 'Corpus Christi, TX', 'Riverside, CA', 'St. Louis, MO', 'Lexington, KY', 'Pittsburgh, PA', 'Anchorage, AK', 'Stockton, CA', 'Cincinnati, OH', 'Saint Paul, MN', 'Toledo, OH', 'Greensboro, NC', 'Newark, NJ', 'Plano, TX', 'Henderson, NV', 'Lincoln, NE', 'Buffalo, NY', 'Jersey City, NJ', 'Chula Vista, CA', 'Orlando, FL', 'Norfolk, VA', 'Chandler, AZ', 'Laredo, TX', 'Madison, WI', 'Durham, NC', 'Lubbock, TX', 'Winston-Salem, NC', 'Garland, TX', 'Glendale, AZ', 'Hialeah, FL', 'Reno, NV', 'Baton Rouge, LA', 'Irvine, CA', 'Chesapeake, VA', 'Irving, TX', 'Scottsdale, AZ', 'North Las Vegas, NV', 'Fremont, CA', 'Boise, ID', 'Richmond, VA', 'San Bernardino, CA', 'Birmingham, AL', 'Spokane, WA', 'Rochester, NY', 'Des Moines, IA', 'Modesto, CA', 'Fayetteville, NC'];
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'Pack Despertar',
  editionLabel: 'Physical Book',
  priceNumber: 6.39,
  priceText: '$6.39'
}, {
  title: 'Edición Digital',
  editionLabel: 'Digital Edition',
  priceNumber: 6.39,
  priceText: '$6.39'
}];
const FALLBACK_WEIGHTS = [0.7, 0.3];
function pickWeighted<T>(items: T[], weights: number[]): T {
  const r = Math.random();
  let acc = 0;
  for (let i = 0; i < items.length; i++) {
    acc += weights[i];
    if (r < acc) return items[i];
  }
  return items[items.length - 1];
}
function parsePrice(text: string): {
  number: number;
  text: string;
} | null {
  if (!text) return null;
  // Match $6.39, 6.39 USD, USD 6.39, €6,39, etc.
  const patterns = [/\$\s?(\d{1,4}(?:[.,]\d{2})?)/, /(\d{1,4}(?:[.,]\d{2})?)\s?USD/i, /USD\s?(\d{1,4}(?:[.,]\d{2})?)/i, /€\s?(\d{1,4}(?:[.,]\d{2})?)/];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) {
      const num = parseFloat(m[1].replace(',', '.'));
      if (!isNaN(num) && num > 0 && num < 100000) {
        return {
          number: num,
          text: m[0].trim()
        };
      }
    }
  }
  return null;
}
function scanJsonLd(): Product[] {
  const products: Product[] = [];
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(s => {
    try {
      const data = JSON.parse(s.textContent || '');
      const arr = Array.isArray(data) ? data : [data];
      arr.forEach((node: any) => {
        if (node['@type'] === 'Product' || node.type === 'Product') {
          const offers = Array.isArray(node.offers) ? node.offers : [node.offers].filter(Boolean);
          const offer = offers[0] || {};
          const priceNum = parseFloat(offer.price);
          products.push({
            title: node.name || 'Product',
            editionLabel: node.category || undefined,
            priceNumber: isNaN(priceNum) ? undefined : priceNum,
            priceText: !isNaN(priceNum) ? `${offer.priceCurrency === 'USD' || !offer.priceCurrency ? '$' : ''}${priceNum.toFixed(2)}${offer.priceCurrency && offer.priceCurrency !== 'USD' ? ' ' + offer.priceCurrency : ''}` : undefined,
            thumbnailUrl: Array.isArray(node.image) ? node.image[0] : node.image
          });
        }
      });
    } catch {
      // ignore parse errors
    }
  });
  return products;
}
function scanDomForProducts(): Product[] {
  // Look for the page's primary product. For this single-product landing page,
  // we look for the hero/cover image + h1 + price patterns.
  const products: Product[] = [];
  // 1. Find heading (h1 or h2 with product name)
  const h1 = document.querySelector('h1');
  const titleCandidate = h1?.textContent?.trim() || '';
  // Try to extract product name from common patterns (e.g. "Introducing The 11PM Spine Reset")
  let title = '';
  const knownTitle = document.body.innerText.match(/(?:Introducing\s+)?(The\s+11PM\s+Spine\s+Reset)/i);
  if (knownTitle) title = knownTitle[1];else if (titleCandidate.length < 80) title = titleCandidate;
  // 2. Find price — look for $X.XX patterns in CTAs or value-stack sections
  const bodyText = document.body.innerText;
  const priceMatches = bodyText.match(/\$\s?\d{1,4}\.\d{2}/g) || [];
  // Choose the most-occurring price (likely the product price)
  const counts: Record<string, number> = {};
  priceMatches.forEach(p => {
    const norm = p.replace(/\s/g, '');
    counts[norm] = (counts[norm] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const topPriceStr = sorted[0]?.[0];
  const priceParsed = topPriceStr ? parsePrice(topPriceStr) : null;
  // 3. Find a thumbnail — first prominent image (book cover, hero image)
  let thumbnailUrl: string | undefined;
  const imgs = Array.from(document.querySelectorAll('img')) as HTMLImageElement[];
  const coverImg = imgs.find(img => /cover|book|product|spine/i.test(img.alt || img.src));
  thumbnailUrl = coverImg?.src || imgs[0]?.src;
  if (title && priceParsed) {
    products.push({
      title,
      editionLabel: 'Digital Edition',
      priceNumber: priceParsed.number,
      priceText: priceParsed.text,
      thumbnailUrl
    });
  }
  return products;
}
function buildCatalog(): Product[] {
  const jsonLd = scanJsonLd();
  if (jsonLd.length > 0) return jsonLd;
  const dom = scanDomForProducts();
  if (dom.length > 0) return dom;
  return FALLBACK_PRODUCTS;
}
function formatPrice(p: Product): string {
  if (p.priceText) return p.priceText;
  if (typeof p.priceNumber === 'number') return `$${p.priceNumber.toFixed(2)}`;
  return '';
}
type Notification = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
export function RecentPurchaseToast() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const catalogRef = useRef<Product[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const isFallbackRef = useRef(false);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    // Detect reduced motion preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    // Build catalog after a tick so the page is fully rendered
    const buildTimer = window.setTimeout(() => {
      const catalog = buildCatalog();
      catalogRef.current = catalog;
      isFallbackRef.current = catalog === FALLBACK_PRODUCTS;
    }, 500);
    let showTimer: number;
    let hideTimer: number;
    let active = true;
    const pickName = (): string => {
      const recent = recentNamesRef.current;
      let tries = 0;
      let candidate = '';
      do {
        const first = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
        const initial = LAST_INITIALS[Math.floor(Math.random() * LAST_INITIALS.length)];
        candidate = `${first} ${initial}`;
        tries++;
      } while (recent.includes(candidate) && tries < 20);
      recentNamesRef.current = [candidate, ...recent].slice(0, 10);
      return candidate;
    };
    const pickProduct = (): Product => {
      const catalog = catalogRef.current;
      if (!catalog.length) return FALLBACK_PRODUCTS[0];
      if (isFallbackRef.current && catalog.length === FALLBACK_PRODUCTS.length) {
        return pickWeighted(catalog, FALLBACK_WEIGHTS);
      }
      return catalog[Math.floor(Math.random() * catalog.length)];
    };
    const showOne = () => {
      if (!active) return;
      const next: Notification = {
        id: ++idRef.current,
        name: pickName(),
        city: US_CITIES[Math.floor(Math.random() * US_CITIES.length)],
        product: pickProduct()
      };
      setCurrent(next);
      hideTimer = window.setTimeout(() => {
        setCurrent(null);
        // Schedule next after random 8-15s
        const nextDelay = 8000 + Math.random() * 7000;
        showTimer = window.setTimeout(showOne, nextDelay);
      }, 4000);
    };
    // First notification at 3s
    showTimer = window.setTimeout(showOne, 3000);
    return () => {
      active = false;
      window.clearTimeout(buildTimer);
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);
  const reduced = prefersReducedMotion.current;
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] pointer-events-none w-[calc(100%-2rem)] max-w-sm" data-id="element-336">
      <AnimatePresence data-id="element-337">
        {current && <motion.div key={current.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: reduced ? 0.15 : 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-navy-800/95 backdrop-blur-md border border-navy-700 rounded-2xl shadow-2xl p-3 flex items-center gap-3" data-id="element-338">
            <div className="relative shrink-0" data-id="element-339">
              {current.product.thumbnailUrl ? <img src={current.product.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-navy-700" data-id="element-340" /> : <div className="w-12 h-12 rounded-lg bg-teal-500/10 border border-teal-500/30 flex items-center justify-center" data-id="element-341">
                  <ShoppingBag className="w-5 h-5 text-teal-400" data-id="element-342" />
                </div>}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-teal-500 text-navy-900 flex items-center justify-center border-2 border-navy-800" data-id="element-343">
                <Check className="w-3 h-3" strokeWidth={3} data-id="element-344" />
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-345">
              <p className="text-xs text-cream-300/60 leading-tight" data-id="element-346">
                <span className="font-semibold text-cream" data-id="element-347">{current.name}</span>{' '}
                in <span className="text-cream-300/90" data-id="element-348">{current.city}</span>
              </p>
              <p className="text-sm text-cream font-medium leading-snug mt-0.5 truncate" data-id="element-349">
                just purchased{' '}
                <span className="text-teal-400" data-id="element-350">
                  {current.product.editionLabel || current.product.title}
                </span>
              </p>
              <p className="text-[11px] text-cream-300/50 mt-0.5" data-id="element-351">
                {formatPrice(current.product)} · a few seconds ago
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}